import { createSlice } from "@reduxjs/toolkit";
import {
  doCreateRestaurant,
  doDeleteRestaurant,
  doFetchRestaurantList,
  doUpdateRestaurant,
} from "./restaurant.action";
import type { Restaurant, Meta } from "src/store/types";

interface RestaurantState {
  restaurants: Restaurant[];
  meta: Meta | null;
  loading: boolean;
  error: string | null;
  searchQuery: string;
}

const initialState: RestaurantState = {
  restaurants: [],
  meta: null,
  loading: false,
  error: null,
  searchQuery: "",
};

export const restaurantSlice = createSlice({
  name: "restaurant",
  initialState,
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearch: (state) => {
      state.searchQuery = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(doFetchRestaurantList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doFetchRestaurantList.fulfilled, (state, action) => {
        state.loading = false;
        state.restaurants = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(doFetchRestaurantList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create
      .addCase(doCreateRestaurant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doCreateRestaurant.fulfilled, (state, action) => {
        if (action.payload && "data" in action.payload) {
          const newRestaurants = action.payload.data;
          if (Array.isArray(newRestaurants)) {
            state.restaurants.unshift(...newRestaurants);
          } else if (newRestaurants) {
            state.restaurants.unshift(newRestaurants);
          }
        }
      })
      .addCase(doCreateRestaurant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update
      .addCase(doUpdateRestaurant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doUpdateRestaurant.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload && Array.isArray(action.payload.data) && action.payload.data.length > 0) {
          const updatedRestaurant = action.payload.data[0];
          state.restaurants = state.restaurants.map((r) =>
            r.id === updatedRestaurant.id ? updatedRestaurant : r
          );
        }
      })
      .addCase(doUpdateRestaurant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete
      .addCase(doDeleteRestaurant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doDeleteRestaurant.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.meta.arg;
        state.restaurants = state.restaurants.filter(
          (r) => String(r.id) !== String(deletedId)
        );
      })
      .addCase(doDeleteRestaurant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSearchQuery, clearSearch } = restaurantSlice.actions;

export default restaurantSlice.reducer;
